import { Nfc, NfcUtils, TypeNameFormat } from '@capawesome-team/capacitor-nfc';
import { Capacitor } from '@capacitor/core';
// @ts-ignore
import { convertAsciiControlChars } from "./Utils.js";
const isPlatformIos = Capacitor.getPlatform() === 'ios';
class ndefReader extends EventTarget {
    isScanning = false;
    readListener;
    scanSessionCanceledListener;
    scanSessionErrorListener;
    onScanSessionError;
    onStopScanning;
    demoTimer;
    onDemoReadSuccess;
    onDemoWriteSuccess;
    async read(eventHandler, demoMode = false) {
        if (this.isScanning) {
            console.warn('ndefReader | read(): Scanning already in progress');
            return;
        }
        this.isScanning = true;
        // On non-iOS platforms, the scan session is permanent
        if (isPlatformIos)
            await Nfc.startScanSession({
                alertMessage: "☐☐☐☐☐"
            });
        this.scanSessionCanceledListener = await Nfc.addListener('scanSessionCanceled', async () => {
            await this.stopScanning("readCancelled");
            console.info('ndefReader | read() - scan session canceled');
        });
        this.scanSessionErrorListener = await Nfc.addListener('scanSessionError', async (event) => {
            await this.stopScanning("readError");
            this.onScanSessionError?.(event);
            console.error('ndefReader | read() - scan session error', event);
        });
        if (demoMode)
            return this.startDemoTimer("read");
        let isReading = false;
        if (this.readListener)
            throw new Error("readListener already set");
        this.readListener = await Nfc.addListener('nfcTagScanned', async (event) => {
            if (isReading) {
                console.log("Reading is already running ... not starting again");
                return;
            }
            isReading = true;
            // Alert anpassen
            if (isPlatformIos) {
                await Nfc.setAlertMessage({
                    message: "◼◼◼◼◼"
                });
            }
            // Respond
            let nfcTag;
            try {
                nfcTag = this.convertNfcTag(event.nfcTag);
                console.info('ndefReader | read()', nfcTag);
                eventHandler(nfcTag);
            }
            catch (error) {
                console.error('ndefReader | read() Error decoding nfc tag:', error);
            }
            await this.stopScanning("readSuccess");
        });
    }
    startDemoTimer(mode) {
        this.demoTimer = setTimeout(async () => {
            if (isPlatformIos)
                await Nfc.setAlertMessage({ message: "◼◼◼◼◼" });
            if (mode === "read")
                this.onDemoReadSuccess?.();
            else
                this.onDemoWriteSuccess?.();
            await this.stopScanning(mode === "read" ? "readSuccess" : "writeSuccess");
        }, 3000);
    }
    async stopScanning(mode) {
        if (this.demoTimer) {
            clearTimeout(this.demoTimer);
            this.demoTimer = undefined;
        }
        this.onStopScanning?.(mode);
        await this.scanSessionCanceledListener?.remove();
        this.scanSessionCanceledListener = undefined;
        await this.scanSessionErrorListener?.remove();
        this.scanSessionErrorListener = undefined;
        await this.readListener?.remove();
        this.readListener = undefined;
        // On non-iOS platforms, the scan session is permanent
        if (isPlatformIos)
            await Nfc.stopScanSession();
        this.isScanning = false;
    }
    convertNfcTag(nfcTag) {
        console.log("nfcTag", nfcTag);
        return {
            serialNumber: this.convertSerialNumber(nfcTag.id),
            message: this.convertMessage(nfcTag.message)
        };
    }
    convertSerialNumber(nfcId) {
        if (nfcId === undefined)
            throw new Error("nfc tag id undefined");
        const utils = new NfcUtils();
        return nfcId
            .map(id => utils.convertNumberToHex({ number: id }).hex)
            .map(hex => hex.padStart(2, '0'))
            .join(':');
    }
    convertMessage(message) {
        if (message === undefined) {
            console.warn("ndefReader | convertMessage: message undefined");
            return {
                records: []
            };
        }
        return {
            records: message.records.map(record => this.convertRecord(record))
        };
    }
    convertRecord(record) {
        if (record.id === undefined)
            throw new Error("Record id undefined");
        if (record.type === undefined)
            throw new Error("Record type undefined");
        if (record.payload === undefined)
            throw new Error("Record payload undefined");
        const utils = new NfcUtils();
        const recordType = this.convertRecordType(record.type, record.tnf);
        return {
            id: utils.convertBytesToString({ bytes: record.id }).text,
            recordType: recordType.recordType,
            mediaType: recordType.mediaType,
            data: new Uint8Array(record.payload)
        };
    }
    convertRecordType(type, tnf) {
        const utils = new NfcUtils();
        const typeText = utils.convertBytesToString({ bytes: type }).text;
        switch (tnf) {
            case TypeNameFormat.WellKnown:
                switch (typeText) {
                    // Url
                    case "U": return { recordType: "url", mediaType: null };
                    // Text
                    case "T": return { recordType: "text", mediaType: null };
                    // Smart Poster
                    case "Sp": return { recordType: "smart-poster", mediaType: null };
                    default: throw new Error("Unsupported well known record type: " + typeText);
                }
            case TypeNameFormat.MimeMedia:
                return {
                    recordType: "mime",
                    mediaType: typeText
                };
            case TypeNameFormat.Empty:
                return {
                    recordType: "empty",
                    mediaType: null
                };
            default: throw new Error("Unsupported record tnf: " + tnf);
        }
    }
    convertToMessage(message) {
        if (!message)
            throw new Error("No message provided for writing");
        return {
            records: message.records.map(record => this.convertToRecord(record))
        };
    }
    convertToRecord(record) {
        const utils = new NfcUtils();
        if (record.data === null)
            throw new Error("Record data is null (" + record.id + ")");
        const bytes = [...record.data];
        switch (record.recordType) {
            case "empty":
                return utils.createNdefEmptyRecord().record;
            case "url":
                const dataValue = record.data;
                if (typeof dataValue !== 'string')
                    throw new Error("Unsupported url: " + typeof dataValue);
                const convertedUrl = convertAsciiControlChars(dataValue);
                if (!convertedUrl.startsWith('http://') && !convertedUrl.startsWith('https://'))
                    throw new Error("Unsupported protocol: " + convertedUrl);
                // If url starts with ascii control char, then do not use createNdefUriRecord to prevent double encoding
                if (dataValue.charCodeAt(0) < 32) {
                    return {
                        id: utils.convertStringToBytes({ text: record.id }).bytes,
                        tnf: TypeNameFormat.WellKnown,
                        type: utils.convertStringToBytes({ text: 'U' }).bytes,
                        payload: utils.convertStringToBytes({ text: dataValue }).bytes
                    };
                }
                else {
                    return utils.createNdefUriRecord({ id: record.id, uri: dataValue }).record;
                }
            case "text":
                return utils.createNdefTextRecord({ id: record.id, text: bytes }).record;
            case "smart-poster":
                throw new Error("Smart poster not implemented");
            case "mime":
                if (record.mediaType === null)
                    throw new Error("mediaType is null");
                return utils.createNdefMimeMediaRecord({ id: record.id, mimeType: record.mediaType, mimeData: bytes }).record;
            default: throw new Error("Unsupported record type: " + record.recordType);
        }
    }
    // Use eventhandler to check readout & calling the write(data) function
    async checkAndWrite(checkTag, onCheckTagOkay, updateWriteData, onDone, demoMode = false) {
        if (this.isScanning) {
            console.warn('ndefReader | checkAndWrite(): Scanning already in progress');
            return;
        }
        this.isScanning = true;
        // On non-iOS platforms, the scan session is permanent
        if (isPlatformIos)
            await Nfc.startScanSession();
        this.scanSessionCanceledListener = await Nfc.addListener('scanSessionCanceled', async () => {
            await this.stopScanning("writeCancelled");
            console.info('ndefReader | checkAndWrite() - scan session canceled');
        });
        this.scanSessionErrorListener = await Nfc.addListener('scanSessionError', async (event) => {
            await this.stopScanning("writeError");
            this.onScanSessionError?.(event);
            console.error('ndefReader | checkAndWrite() - scan session error', event);
        });
        if (demoMode)
            return this.startDemoTimer("write");
        let isWriting = false;
        let isWritingFinished = false;
        if (this.readListener)
            throw new Error("readListener already set");
        this.readListener = await Nfc.addListener('nfcTagScanned', async (event) => {
            if (isWriting) {
                console.log("Writing is already running ... not starting again");
                return;
            }
            isWriting = true;
            if (isPlatformIos) {
                await Nfc.setAlertMessage({
                    message: "◼☐☐☐☐"
                });
                function setAlertMessageAfterTimeout(message, timeout) {
                    setTimeout(() => {
                        if (!isWritingFinished) {
                            Nfc.setAlertMessage({ message: message });
                        }
                    }, timeout);
                }
                setAlertMessageAfterTimeout("◼◼☐☐☐", 1500);
                setAlertMessageAfterTimeout("◼◼◼☐☐", 3000);
                setAlertMessageAfterTimeout("◼◼◼◼☐", 4500);
            }
            // Check nfc tag
            const nfcTag = this.convertNfcTag(event.nfcTag);
            const checkTag_res = checkTag(nfcTag);
            console.info(`ndefReader | checkAndWrite() | checkTag(): ${checkTag_res.status}`);
            // Wenn das Tag okay ist, dann schreiben
            let writeSuccess = false;
            if (checkTag_res.status === 'tagOkay') {
                onCheckTagOkay();
                const message = updateWriteData();
                console.log("messageToConvert", message);
                const writeMesssage = this.convertToMessage(message);
                console.log("writeMesssage", writeMesssage);
                try {
                    await Nfc.write({
                        message: writeMesssage
                    });
                    isWritingFinished = true;
                    if (isPlatformIos) {
                        await Nfc.setAlertMessage({
                            message: "◼◼◼◼◼"
                        });
                    }
                    writeSuccess = true;
                }
                catch (error) {
                    console.error('ndefReader | checkAndWrite() | write():', error);
                    console.error('ndefReader | checkAndWrite() | write():', error?.message);
                }
            }
            isWritingFinished = true;
            await this.stopScanning("writeSuccess");
            onDone({
                readNfcTag: nfcTag,
                checkTag_res: checkTag_res,
                success: writeSuccess
            });
        });
    }
    async error(eventHandler) {
        this.onScanSessionError = eventHandler;
    }
    async addStopScanningListener(eventHandler) {
        this.onStopScanning = eventHandler;
    }
    addDemoModeHandlers(onReadSuccess, onWriteSuccess) {
        this.onDemoReadSuccess = onReadSuccess;
        this.onDemoWriteSuccess = onWriteSuccess;
    }
    async isAvailable() {
        const result = await Nfc.isAvailable();
        return result.nfc;
    }
    async checkPermissions() {
        const result = await Nfc.checkPermissions();
        return result.nfc;
    }
    async check() {
        await Nfc.requestPermissions();
    }
    async startBackgroundScan() {
        // Only supported on Web
        if (isPlatformIos)
            return;
        // One permanent scan session on web
        await Nfc.startScanSession();
    }
}
export default ndefReader;
